import { useEffect, useRef, useState } from "react";

/**
 * SignatureCanvas — touch + mouse drawing for digital signature.
 *
 * Props:
 *   onChange(dataUrl: string|null)  — called when the user finishes a stroke;
 *                                     dataUrl is the PNG (base64 data URL)
 *                                     or null when empty/cleared.
 *
 * Behavior:
 *   - Auto-resizes to container width (height fixed at 160px)
 *   - Pointer events (touch + mouse) unified
 *   - "Tyhjennä" clears the canvas
 *   - Internally tracks whether anything has been drawn (`isEmpty`)
 */
export default function SignatureCanvas({ onChange, label = "Allekirjoita tähän" }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  // Resize canvas to match its CSS size with proper device-pixel scaling.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2.4;
      ctx.strokeStyle = "#0f172a"; // slate-900
    };
    resize();
    // Re-init when orientation/window resize happens
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  const getPoint = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches && e.touches[0];
    const clientX = t ? t.clientX : e.clientX;
    const clientY = t ? t.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const onDown = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const p = getPoint(e);
    lastPointRef.current = p;
    const ctx = canvasRef.current.getContext("2d");
    // dot for single-tap
    ctx.beginPath();
    ctx.arc(p.x, p.y, 1.2, 0, Math.PI * 2);
    ctx.fillStyle = "#0f172a";
    ctx.fill();
    setIsEmpty(false);
  };

  const onMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = getPoint(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
  };

  const onUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange?.(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setIsEmpty(true);
    onChange?.(null);
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-slate-500">{label}</span>
        <button
          type="button"
          onClick={clear}
          disabled={isEmpty}
          className="text-xs text-slate-500 hover:text-slate-900 disabled:opacity-40"
          data-testid="signature-clear"
        >
          Tyhjennä
        </button>
      </div>
      <div className="relative w-full h-40 rounded-xl border-2 border-dashed border-slate-300 bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full touch-none cursor-crosshair"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onUp}
          onTouchStart={onDown}
          onTouchMove={onMove}
          onTouchEnd={onUp}
          data-testid="signature-canvas"
        />
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-xs text-slate-300">✍️ Piirrä sormella tai hiirellä</span>
          </div>
        )}
      </div>
    </div>
  );
}
